import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, XCircle, ClockCounterClockwise, Warning, Package } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { formatJST, formatJSTDate } from '@/utils/datetime';

const API_ROOT = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const YEN = new Intl.NumberFormat('ja-JP');

/**
 * Magic-link approval landing page. Renders the PO summary + line items +
 * two buttons: 承認 / 却下. Neither requires the visitor to be authenticated
 * — the URL token itself is the auth. Token is single-use (marked decided
 * on first click).
 */
export default function ApprovalToken() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [comment, setComment] = useState('');
  const [email, setEmail] = useState('');
  const [showReject, setShowReject] = useState(false);

  useEffect(() => {
    axios.get(`${API_ROOT}/shizai/approvals/token/${token}`)
      .then((r) => {
        setData(r.data);
        if (r.data.approval?.approver_email) setEmail(r.data.approval.approver_email);
      })
      .catch((e) => setError(e?.response?.data?.detail || '承認リンクを開けません'))
      .finally(() => setLoading(false));
  }, [token]);

  const decide = async (kind) => {
    setBusy(true);
    try {
      await axios.post(`${API_ROOT}/shizai/approvals/token/${token}/${kind}`, {
        comment: comment || null,
        decided_by_email: email || null,
      });
      toast.success(kind === 'approve' ? '承認しました' : '却下しました');
      // Refresh view
      const r = await axios.get(`${API_ROOT}/shizai/approvals/token/${token}`);
      setData(r.data);
      setShowReject(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || '処理に失敗しました');
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="text-zinc-500">読み込み中...</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl border border-red-200 shadow p-8 max-w-md text-center">
          <XCircle size={48} weight="duotone" className="text-red-500 mx-auto mb-2" />
          <p className="text-red-700 font-semibold">{error}</p>
        </div>
      </div>
    );
  }

  const { approval, order, already_decided, expired } = data || {};
  const decided = already_decided || approval?.status !== 'pending';
  const isExpired = expired || approval?.status === 'expired';

  return (
    <div className="min-h-screen bg-zinc-50 p-4" data-testid="approval-landing">
      <div className="max-w-3xl mx-auto space-y-4">
        {/* Header */}
        <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-5">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-xl font-semibold text-zinc-900">発注承認</h1>
              <p className="text-sm text-zinc-500">依頼: {approval?.rule_name} · {formatJSTDate(approval?.requested_at)}</p>
            </div>
            <Badge variant="outline" className={
              approval?.status === 'approved' ? 'bg-emerald-100 text-emerald-700 border-emerald-300' :
              approval?.status === 'rejected' ? 'bg-red-100 text-red-700 border-red-300' :
              approval?.status === 'expired' ? 'bg-zinc-100 text-zinc-500 border-zinc-300' :
              'bg-amber-100 text-amber-700 border-amber-300'
            } data-testid="approval-status">
              {approval?.status === 'approved' && '承認済'}
              {approval?.status === 'rejected' && '却下'}
              {approval?.status === 'expired' && '期限切れ'}
              {approval?.status === 'pending' && '承認待ち'}
            </Badge>
          </div>
        </div>

        {isExpired && (
          <div className="bg-zinc-50 border-2 border-zinc-200 rounded-xl p-4 flex items-center gap-3" data-testid="approval-expired">
            <ClockCounterClockwise size={24} className="text-zinc-500" />
            <p className="text-sm text-zinc-600">
              この承認リンクは有効期限（7日間）が過ぎたため無効です。バイヤーに新しい承認依頼を作成してもらってください。
            </p>
          </div>
        )}

        {decided && !isExpired && (
          <div className={`rounded-xl p-4 flex items-center gap-3 ${
            approval?.status === 'approved' ? 'bg-emerald-50 border-2 border-emerald-200'
            : 'bg-red-50 border-2 border-red-200'
          }`} data-testid="approval-decided">
            {approval?.status === 'approved'
              ? <CheckCircle size={28} weight="duotone" className="text-emerald-500" />
              : <XCircle size={28} weight="duotone" className="text-red-500" />
            }
            <div>
              <p className="text-sm font-semibold">
                {approval?.status === 'approved' ? '承認済み' : '却下済み'} — {approval?.decided_by_name}
              </p>
              <p className="text-xs text-zinc-600">
                {formatJST(approval?.decided_at)}
                {approval?.reject_reason && ` · 理由: ${approval.reject_reason}`}
              </p>
            </div>
          </div>
        )}

        {/* Order summary */}
        <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-5">
          <h2 className="text-sm font-semibold text-zinc-500 mb-2">発注内容</h2>
          <div className="flex items-baseline gap-3 flex-wrap mb-3">
            <span className="font-mono text-xl font-bold text-emerald-600">{approval?.order_no}</span>
            <span className="text-2xl font-bold text-zinc-800 tabular-nums">¥{YEN.format(approval?.order_total || 0)}</span>
          </div>
          <div className="text-sm text-zinc-700 space-y-1">
            <p><b>仕入先:</b> {(approval?.supplier_snapshot || {}).name || '—'}</p>
            <p><b>依頼者:</b> {approval?.requested_by_name || '—'}</p>
            {order?.notes && <p><b>備考:</b> {order.notes}</p>}
          </div>
          {order?.lines?.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-semibold text-zinc-500 mb-2 flex items-center gap-1">
                <Package size={12} weight="duotone" />明細 ({order.lines.length}件)
              </p>
              <div className="max-h-48 overflow-y-auto border border-zinc-100 rounded">
                {order.lines.map((ln, i) => (
                  <div key={i} className="text-xs px-2 py-1.5 border-b border-zinc-200 last:border-b-0 flex items-center gap-2">
                    <span className="w-6 text-zinc-400 tabular-nums">#{i + 1}</span>
                    <span className="flex-1 truncate">{ln.name || ln.part_no || '—'}</span>
                    <span className="tabular-nums text-zinc-500">{ln.qty} {ln.uom}</span>
                    <span className="tabular-nums font-semibold w-20 text-right">¥{YEN.format(ln.line_total || 0)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Decision panel */}
        {!decided && !isExpired && (
          <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-5 space-y-3" data-testid="approval-decision-panel">
            <div>
              <Label className="text-xs font-semibold text-zinc-600">承認者のメールアドレス（監査用）</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="approval-email"
                className="h-10 mt-1" />
            </div>
            {showReject ? (
              <div className="space-y-3">
                <div>
                  <Label className="text-xs font-semibold text-zinc-600">却下理由<span className="text-red-500 ml-0.5">*</span></Label>
                  <Input value={comment} onChange={(e) => setComment(e.target.value)}
                    data-testid="approval-reject-reason"
                    placeholder="例: 予算超過、仕様変更が必要"
                    className="h-10 mt-1" />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowReject(false)} className="flex-1">戻る</Button>
                  <Button onClick={() => decide('reject')} disabled={busy || !comment.trim()}
                    data-testid="approval-reject-confirm"
                    className="flex-1 bg-red-500 hover:bg-red-600">
                    {busy ? '処理中…' : '却下を確定'}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div>
                  <Label className="text-xs font-semibold text-zinc-600">コメント（任意）</Label>
                  <Input value={comment} onChange={(e) => setComment(e.target.value)}
                    data-testid="approval-comment"
                    className="h-10 mt-1" />
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => setShowReject(true)} variant="outline"
                    data-testid="approval-reject-btn"
                    className="flex-1 border-red-300 text-red-700 hover:bg-red-50">
                    <XCircle size={16} className="mr-1.5" />却下
                  </Button>
                  <Button onClick={() => decide('approve')} disabled={busy}
                    data-testid="approval-approve-btn"
                    className="flex-1 bg-teal-700 hover:bg-teal-800 text-white shadow-md h-12 text-base font-bold">
                    <CheckCircle size={18} weight="duotone" className="mr-1.5" />
                    {busy ? '処理中…' : '承認'}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
